import {TextareaVariable} from '../../../../../gd-components/textarea-with-variables/types'
import FieldsFactory1 from '../../../../fields-factory'
import {FieldGroup, Field1GroupTitle} from '../../../../@group'
import {SystemColumnType} from '../../../../../gd-modules/manager/columns/types'

// Same group set/order as the Table View "add column" popover
// (see gd-modules/tasks-view/core/manager/columns/groups.ts), with
// Custom fields last.
const GROUP_ORDER : FieldGroup[] = [
    FieldGroup.MAIN,
    FieldGroup.USERS,
    FieldGroup.DATES,
    FieldGroup.ESTIMATE,
    FieldGroup.TIME,
    FieldGroup.DEPENDENCIES,
    FieldGroup.FINANCE,
    FieldGroup.CRM,
    FieldGroup.CUSTOM_FIELDS
]

// Fields3 injections below have no FIELDS1 entry to read a group/columnId from,
// so their Table View group and columnId (see groups.ts) are mirrored here by hand.
const FIELD3_GROUP : {[fieldId: string]: FieldGroup} = {
    estimateTasks         : FieldGroup.ESTIMATE,
    estimatedCostInternal : FieldGroup.FINANCE,
    estimatedCostExternal : FieldGroup.FINANCE
}

const FIELD3_COLUMN_ID : {[fieldId: string]: string} = {
    estimateTasks         : SystemColumnType.PROJECT_ESTIMATE_TASKS,
    estimatedCostInternal : SystemColumnType.ESTIMATED_COST_INTERNAL,
    estimatedCostExternal : SystemColumnType.ESTIMATED_COST_EXTERNAL
}

// Position of each column within its group, taken from the Table View
// "add column" popover order, so formula tokens list fields in the same
// order within each group.
//
// Loaded lazily (not as a static import) because ColumnsGroups_V2 reads
// gd.modules.*.enabled at module-eval time — formula-tokens.ts gets pulled
// in very early (custom field type registration) before `gd` exists, so a
// static import would crash on load.
let COLUMN_ORDER : {[columnId: string]: number} | null = null

function getColumnOrder(): {[columnId: string]: number} {
    if (COLUMN_ORDER) {
        return COLUMN_ORDER
    }

    const ColumnsGroups_V2 = require('../../../../../gd-modules/tasks-view/core/manager/columns/groups').default

    const order : {[columnId: string]: number} = {}

    _.forEach(ColumnsGroups_V2, group=>{
        let index = 0

        _.forEach(group.columnsIds, columnId=>{
            if (columnId !== 'separator') {
                order[columnId] = index
                index++
            }

            return
        })

        return
    })

    COLUMN_ORDER = order

    return order
}

// Builds one TextareaVariable per formula-compatible field, grouped the
// same way Table View groups columns in its "add column" popover
// (General, Users, Dates, Estimate, Time & Progress, Dependencies,
// Finance, CRM, Custom fields). Within each group, fields are ordered the
// same way as in that popover (see COLUMN_ORDER), falling back to label
// order for fields with no popover position (e.g. custom fields).
//
// `token` mirrors what `optionSelect` used to insert into the formula
// string — Fields3 fields use their fixed `variableName` (e.g.
// `{PROJECT-ESTIMATE-TASKS}`), Field1 fields use `{<label>}` so
// existing stored formulas keep parsing exactly as before.
export function buildFormulaTokens(): TextareaVariable[] {
    let fieldsIds : any[] = []

    // add all field1 system fields
    _.mapKeys(gd.const.fields.type, value=>{
        fieldsIds.push(value)
        return
    })

    // add all custom fields
    _.map(gd.session.customFields.filterByCompany(gd.companyId).models, cf=>{
        fieldsIds.push(cf.id)
        return
    })

    // I guess we remove this fields for all fields here
    fieldsIds = _.filter(fieldsIds, id=>{
        return id && ![
            gd.const.fields.type.SYSTEM_STATUS,
            gd.const.fields.type.PARENT_TASK_ID,
            gd.const.fields.type.CUSTOM_FIELD,
            gd.const.fields.type.PROJECT_TYPE,
            gd.const.fields.type.SUBTASKS_COUNTER,
            gd.const.fields.type.CHECK_BOX
        ].includes(id)
    })

    let fields : any[] = []

    _.map(fieldsIds, id=>{
        const field = FieldsFactory1.get(id)

        if (field && field.isFormulaCompatible()) {
            fields.push(field)
        }

        return
    })

    // Fields3 Injection
    // dont forget to update formulaField3Factory
    fields.push(gd.fields.project.estimateTasks)
    fields.push(gd.fields.project.estimatedCostInternal)
    fields.push(gd.fields.project.estimatedCostExternal)

    const getGroup = (field: any): FieldGroup => {
        if (field.customField) {
            return FieldGroup.CUSTOM_FIELDS
        }

        if (field.fieldId) {
            return FIELD3_GROUP[field.fieldId] || FieldGroup.MAIN
        }

        return gd.const.fields[field.type]?.group || FieldGroup.MAIN
    }

    const fieldsByGroup = _.groupBy(fields, getGroup)

    const getColumnId = (field: any): string | undefined => {
        if (field.customField) {
            return undefined
        }

        if (field.fieldId) {
            return FIELD3_COLUMN_ID[field.fieldId]
        }

        return gd.const.fields[field.type]?.columnId
    }

    const getIcon = (field: any, isField3: boolean): string => {
        if (field.customField) {
            return field.systemIcon || ''
        }

        if (isField3) {
            return field.icon || ''
        }

        return gd.const.fields[field.type]?.icon || ''
    }

    const toToken = (field: any, group: string): TextareaVariable => {
        const isField3 = !!field.fieldId && !field.customField

        return {
            label : field.label,
            icon  : getIcon(field, isField3),
            token : isField3 ? field.variableName : '{{0}}'.f(field.label),
            group : group
        }
    }

    let tokens : TextareaVariable[] = []

    _.map(GROUP_ORDER, groupId=>{
        const groupFields = fieldsByGroup[groupId]

        if (!groupFields || !groupFields.length) {
            return
        }

        const columnOrder = getColumnOrder()

        const sortedFields = _.sortBy(groupFields, [
            field => {
                const columnId = getColumnId(field)
                const order = columnId !== undefined ? columnOrder[columnId] : undefined
                return order !== undefined ? order : Number.MAX_SAFE_INTEGER
            },
            'label'
        ])
        const groupLabel = Field1GroupTitle[groupId]

        tokens = tokens.concat(_.map(sortedFields, field=>{
            return toToken(field, groupLabel)
        }))

        return
    })

    return tokens
}
